import { Box3, Vector2Tuple } from 'three';

import { Schema, SchemaFieldFurniture, SchemaValues } from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import {
  FArrangementGrid,
  FArrangementGridSchema,
  FArrangementGridSchemaFields,
  FArrangementGridValues,
} from './FArrangementGrid.js';

const bbox = new Box3();
const TAU = Math.PI * 2;

export interface FArrangementTheaterSchemaFields extends FArrangementGridSchemaFields {
  chairType: SchemaFieldFurniture;
}

export interface FArrangementTheaterSchema extends FArrangementGridSchema {
  fields: FArrangementTheaterSchemaFields;
}

export interface FArrangementTheaterValues {}

export type FArrangementTheaterAttributes = Partial<SchemaValues<FArrangementTheaterSchemaFields>> &
  FArrangementGridValues &
  FArrangementTheaterValues;

export class FArrangementTheater<
  TSchema extends FArrangementTheaterSchema = FArrangementTheaterSchema,
  TValues extends FArrangementTheaterValues = FArrangementTheaterValues,
  TFCompositionSchema extends Schema = Schema,
  TFCompositionValues extends Record<string, any> = {}
> extends FArrangementGrid<
  FArrangementTheaterSchema,
  FArrangementTheaterValues,
  TFCompositionSchema,
  TFCompositionValues
> {
  constructor(
    schema: TSchema,
    initialValues?: FArrangementTheaterAttributes & TValues & TFCompositionValues
  ) {
    super(schema, {
      maxElements: Infinity,
      elementSize: [0, 0],
      ...(initialValues as any),
    });
    this.setValue('getMaxElements', this.getMaxElements);
  }

  getMaxElements = () => {
    return this.computeNodes(true).length;
  };

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    let { elements } = this.getValues();
    return this.getSafeIterations(
      Math.min(ignoreMaxElements ? Infinity : elements, maxColumns * maxRows)
    );
  }

  getNode(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ) {
    const { elementRotation, orientation } =
      this.getValues() as SchemaValues<FArrangementTheaterSchemaFields>;

    const theta = orientation * TAU;

    const { position } = params;

    const [id] = this.getNodeTypes();
    return {
      id: id,
      uuid: this.getNodeUuid(id, idx),
      position: position,
      rotation: -theta + elementRotation * TAU,
    };
  }

  protected getCompositionNodes(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
    idx: number
  ): FInstanceNode[] {
    const { elementRotation, orientation } = this.getValues();

    const theta = orientation * TAU;

    const { position } = params;

    const [id] = this.getNodeTypes();
    return [
      {
        id: id,
        type: 'chair',
        position: position,
        rotation: -theta + elementRotation * TAU,
      },
    ];
  }

  getNodeTypes(): number[] {
    const { chairType } = this.getValues() as SchemaValues<FArrangementTheaterSchemaFields> &
      FArrangementTheaterValues;

    if (!chairType) {
      throw new Error('chairType is required');
    }

    return [chairType];
  }

  getInstanceBoundingBox(): Box3 {
    const [chair] = this.getObjects();

    return bbox.setFromObject(chair);
  }
}
